import { Client, type StompSubscription, type IMessage } from '@stomp/stompjs';
import { API_COMMON } from '~/constants/property_manager/api-common';
import { v4 as uuidv4 } from 'uuid';

class NotificationService {
  private stompClient: Client | null = null;
  private connected = false;
  private subscriptions: Map<string, StompSubscription> = new Map();
  private prioritySubscriptions: Map<string, StompSubscription> = new Map();

  private currentRequestId: String | null = null;

  async connect(connectedCallback?: () => void, subscribePaths: string[] = [], callback?: (message: IMessage) => void): Promise<void> {
    const requestId = uuidv4();

    this.currentRequestId = requestId;

    if (this.stompClient) {
      await this.disconnect();
    }

    if (requestId != this.currentRequestId) {
      return;
    }

    if (this.connected) return;
    const config = useRuntimeConfig();
    const BASE_URL = config.public.API_URL;
    const { public: { API_BASE_PATH } }= useRuntimeConfig();

    const WS_URL = BASE_URL.replace(/^http/, 'ws') + "/" + API_BASE_PATH + '/notification';
  
    this.stompClient = new Client({
      brokerURL: WS_URL,
      reconnectDelay: 2000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      beforeConnect: async () => {
        await useSocketApi.post(API_COMMON.PROP_API_REFRESH_ACCESS_TOKEN);
        const authStore  = usePropertyUserStore();
        const token  = `Bearer ${authStore.accessToken}`;
        this.stompClient!.connectHeaders = {
          Authorization: token,
          'username': encodeURIComponent(authStore?.userInfo?.name || '')
        }
      },
      onConnect: () => {
        this.connected = true;
        if (typeof connectedCallback === 'function') {
          connectedCallback();
        }

        if (Array.isArray(subscribePaths) && subscribePaths.length > 0 && typeof callback === 'function') {
          subscribePaths.forEach((path:string) => this.subscribeToTopic(path, callback, true));
        }
      },
      onDisconnect: () => {
        this.connected = false;
      },
      onStompError: (frame) => {
      }
    });

    this.stompClient.activate();
  }

  subscribeToTopic(subscribePath: string, callback: (message: IMessage) => void, isPriority: boolean = false): StompSubscription | null {
    if (!this.stompClient || !this.connected) return null;

    const subscription = this.stompClient.subscribe('/topic/' + subscribePath, callback);
    if (isPriority) {
      this.prioritySubscriptions.set(subscribePath, subscription);
    } else {
      this.subscriptions.set(subscribePath, subscription);
    }
    return subscription;
  }

  unsubscribeAll(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions.clear();

    this.prioritySubscriptions.forEach(sub => sub.unsubscribe());
    this.prioritySubscriptions.clear();
  }

  unsubscribeNormalSubscriptions(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions.clear();
  }

  unsubscribeByPath(subscribePath: string): void {
    // Try normal subscriptions
    if (this.subscriptions.has(subscribePath)) {
      const sub = this.subscriptions.get(subscribePath);
      sub?.unsubscribe();
      this.subscriptions.delete(subscribePath);
      return;
    }

    // Try priority subscriptions
    if (this.prioritySubscriptions.has(subscribePath)) {
      const sub = this.prioritySubscriptions.get(subscribePath);
      sub?.unsubscribe();
      this.prioritySubscriptions.delete(subscribePath);
      return;
    }
  }

  async disconnect(): Promise<void> {
    if (!this.stompClient) return;
    this.unsubscribeAll();
    try {
      await this.stompClient.deactivate();
    } catch (e) {
      console.warn('Deactivate error:', e);
    }

    this.stompClient = null;
    this.connected = false;
  }
}

export default new NotificationService();